import { Kind, type DocumentNode, type OperationDefinitionNode } from 'graphql'
import { chunk, uniq } from 'lodash'
import { graphql } from 'app/generated/gql'
import { createBatchLoader } from 'app/lib/batchLoader'
import { makeAutoObservable, runInAction } from 'mobx'
import type { Client } from '@urql/core'

const GET_SETTING = graphql(`
  query UserSettings($key: String!) {
    userSettings(key: $key)
  }
`)

const SAVE_SETTING = graphql(`
  mutation SaveUserSettings($key: String!, $value: String!) {
    saveUserSettings(key: $key, value: $value) {
      key
      value
    }
  }
`)

// Keys read in the same tick go out as one request. A page mounts a hook per
// table column setting, so this was one round trip per key (24 on the contacts
// page). Each key becomes an aliased `userSettings` field with its own variable,
// so the API needs no batch query.
const BATCH_SIZE = 50

// urql keeps every document object it prints for the life of the page, so one
// document per size (at most BATCH_SIZE) is reused instead of a new one per request.
const batchDocuments = new Map<number, DocumentNode>()

function batchDocument(count: number): DocumentNode {
  const cached = batchDocuments.get(count)
  if (cached) return cached
  const operation = GET_SETTING.definitions.find(
    (definition): definition is OperationDefinitionNode => definition.kind === Kind.OPERATION_DEFINITION,
  )!
  const field = operation.selectionSet.selections[0]
  if (field.kind !== Kind.FIELD) throw new Error('UserSettings must select a field')
  const indexes = Array.from({ length: count }, (_, i) => i)
  // A fresh document, not a spread of GET_SETTING: urql stamps `__key` and `loc` on
  // documents it has sent, and a copied `__key` makes urql send UserSettings with
  // these `k` variables ("$key was not provided").
  const document: DocumentNode = {
    kind: Kind.DOCUMENT,
    definitions: [
      {
        ...operation,
        name: { kind: Kind.NAME, value: 'UserSettingsBatch' },
        variableDefinitions: indexes.map((i) => ({
          ...operation.variableDefinitions![0],
          variable: { kind: Kind.VARIABLE, name: { kind: Kind.NAME, value: `k${i}` } },
        })),
        selectionSet: {
          kind: Kind.SELECTION_SET,
          selections: indexes.map((i) => ({
            ...field,
            alias: { kind: Kind.NAME, value: `s${i}` },
            arguments: [
              { ...field.arguments![0], value: { kind: Kind.VARIABLE, name: { kind: Kind.NAME, value: `k${i}` } } },
            ],
          })),
        },
      },
    ],
  }
  batchDocuments.set(count, document)
  return document
}

export class UserSettingStore {
  settings: Record<string, string | undefined> = {}
  loading = false
  private client: Client
  // `null` means the API answered and the key is unset. A key with no answer (the
  // request failed) is left out, so the next read asks again.
  private loader = createBatchLoader<string, string | null>(async (keys) => {
    const values = new Map<string, string | null>()
    await Promise.all(chunk(uniq(keys), BATCH_SIZE).map((batch) => this.fetchBatch(batch, values)))
    return values
  })

  constructor(client: Client) {
    this.client = client
    makeAutoObservable(this, { client: false, loader: false } as never, { autoBind: true })
  }

  private async fetchBatch(keys: string[], values: Map<string, string | null>) {
    if (keys.length === 1) {
      const result = await this.client.query(GET_SETTING, { key: keys[0] }).toPromise()
      if (result.data) values.set(keys[0], result.data.userSettings ?? null)
      return
    }
    const variables = Object.fromEntries(keys.map((key, i) => [`k${i}`, key]))
    const result = await this.client
      .query<Record<string, string | null | undefined>>(batchDocument(keys.length), variables)
      .toPromise()
    if (!result.data) return
    keys.forEach((key, i) => values.set(key, result.data?.[`s${i}`] ?? null))
  }

  async get(key: string): Promise<string | undefined> {
    if (key in this.settings) return this.settings[key]
    this.loading = true
    const value = await this.loader.load(key)
    runInAction(() => {
      this.loading = false
      // A save that landed while this query was in flight is newer than the value
      // the query returned, so the stale read must not overwrite it.
      if (value !== undefined && !(key in this.settings)) this.settings[key] = value ?? undefined
    })
    return this.settings[key]
  }

  /**
   * Writes the new value into the cache before the mutation resolves, so anything
   * observing this setting reacts on the click instead of a round-trip later, and
   * rolls back if the save fails.
   */
  async save(key: string, value: string): Promise<boolean> {
    const previous = this.settings[key]
    runInAction(() => {
      this.settings[key] = value
    })
    const result = await this.client.mutation(SAVE_SETTING, { key, value }).toPromise()
    if (result.error) {
      runInAction(() => {
        this.settings[key] = previous
      })
    }
    return !result.error
  }

  reset() {
    this.settings = {}
    this.loading = false
    this.loader.clear()
  }
}
